import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { DEFAULT_HEADER_HEIGHT } from '@core/components/header/header.model';
import { AccountService } from '@core/services/account.service';
import { NotificationType, UserNotificationItem, UserNotificationStatus } from '@shared/models';
import { BehaviorSubject, merge, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, filter, map, mapTo, switchMap, tap } from 'rxjs/operators';
import { NotificationsApiService } from './notifications-api.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationsCenterService {
  get pushNotifications$() {
    return this.pushNotificationsSubj$
      .asObservable()
      .pipe(
        map(data => data.filter(i => i.type === NotificationType.Alert || i.type === NotificationType.Notification))
      );
  }

  get urgentAlerts$() {
    return this.pushNotificationsSubj$
      .asObservable()
      .pipe(map(data => data.filter(i => i.type === NotificationType['Urgent alert'])));
  }

  get notificationsForCenter$() {
    return this.notificationsForCenterSubj$.asObservable();
  }

  get notificationsAndHeaderHeight$(): Observable<number> {
    return this.notificationsAndHeaderHeightSubj$.asObservable();
  }

  get isShowNotifications$(): Observable<boolean> {
    return this.isShowNotificationSubj$.asObservable();
  }

  private pushNotificationsSubj$ = new BehaviorSubject<UserNotificationItem[]>([]);
  private notificationsForCenterSubj$ = new BehaviorSubject<UserNotificationItem[]>([]);
  private notificationsForCenterTrigger$ = new Subject<unknown>();
  private subs: Subscription;
  private pollingSubs: Subscription;
  private country: string;
  private notificationsAndHeaderHeightSubj$ = new BehaviorSubject<number>(DEFAULT_HEADER_HEIGHT);
  private isShowNotificationSubj$ = new BehaviorSubject<boolean>(false);
  private recentlyDeleted = new Map<string, any>();

  constructor(
    private account: AccountService,
    private notificationsApi: NotificationsApiService,
    private router: Router
  ) {
    // @ts-ignore
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.isShowNotificationSubj$.next(!event.url.includes('admin'));
      }
    });
  }

  public setNotificationsAndHeaderHeight(height: number) {
    this.notificationsAndHeaderHeightSubj$.next(height);
  }

  public getUrl(isUpdate: boolean) {
    if (isUpdate) {
      this.isShowNotificationSubj$.next(!this.router.url.includes('admin'));
    }
  }

  // Calls subscribe Api and runs Pollings for Push notifications and notifications for center
  public subscribe(): void {
    this.unsubscribe();
    this.subs = this.account.countryCode$
      .pipe(switchMap(countryCode => this.notificationsApi.subscribe(countryCode).pipe(mapTo(countryCode))))
      .subscribe(countryCode => {
        if (this.country !== countryCode) {
          this.pushNotificationsSubj$.next([]);
        }
        this.country = countryCode;

        if (this.pollingSubs) {
          this.pollingSubs.unsubscribe();
        }
        this.pollingSubs = new Subscription();

        this.pollingSubs.add(
          this.pushPolling(countryCode).subscribe(newNotifications => {
            const ids = this.pushNotificationsSubj$.getValue().map(i => i.notificationId);
            newNotifications = newNotifications.filter(i => !ids.includes(i.notificationId));
            this.pushNotificationsSubj$.next([...newNotifications, ...this.pushNotificationsSubj$.getValue()]);
          })
        );

        this.pollingSubs.add(
          this.centerPolling(countryCode).subscribe(data => {
            this.notificationsForCenterSubj$.next(data);
          })
        );
      });
  }

  public unsubscribe(): void {
    if (this.subs) {
      this.subs.unsubscribe();
      this.subs = null;
    }
    if (this.pollingSubs) {
      this.pollingSubs.unsubscribe();
      this.pollingSubs = null;
    }
    if (this.recentlyDeleted.size) {
      this.clearRecentlyDeleted();
    }
  }

  public hidePushAll(): void {
    const ids = this.pushNotificationsSubj$
      .getValue()
      .filter(n => n.type === NotificationType.Alert || n.type === NotificationType.Notification)
      .map(i => i.notificationId);
    this.hidePushBulk(ids);
  }

  public dismissPushAll(): void {
    const ids = this.pushNotificationsSubj$
      .getValue()
      .filter(n => n.type === NotificationType.Alert || n.type === NotificationType.Notification)
      .map(i => i.notificationId);
    this.dismissPushBulk(ids);
  }

  public dismissFromCenterAll(): void {
    const ids = this.notificationsForCenterSubj$.getValue().map(i => i.notificationId);
    this.notificationsApi.dismissBulk(ids, true).subscribe(() => {
      this.reloadNotificationsForCenter();
    });
  }

  public hidePushBulk(notificationIds: string[]): void {
    const items = this.pushNotificationsSubj$.getValue().filter(i => notificationIds.includes(i.notificationId));
    const toHideIds = items.filter(i => i.status === UserNotificationStatus.New).map(i => i.notificationId);

    (toHideIds.length ? this.notificationsApi.hideBulk(toHideIds, true) : of({})).subscribe(() => {
      this.reloadNotificationsForCenter();
      this.pushNotificationsSubj$.next(
        this.pushNotificationsSubj$.getValue().filter(i => !notificationIds.includes(i.notificationId))
      );
    });
  }

  public dismissPushBulk(notificationIds: string[]): void {
    this.notificationsApi.dismissBulk(notificationIds, true).subscribe(() => {
      this.reloadNotificationsForCenter();
      this.pushNotificationsSubj$.next(
        this.pushNotificationsSubj$.getValue().filter(i => !notificationIds.includes(i.notificationId))
      );
      this.reloadNotificationsForCenter();
    });
  }

  public dismissFromCenter(id: string): void {
    this.notificationsApi.dismissBulk([id], true).subscribe(() => {
      this.recentlyDeleted.set(
        id,
        setTimeout(() => {
          this.recentlyDeleted.delete(id);
          this.notificationsForCenterSubj$.next(
            this.notificationsForCenterSubj$.getValue().filter(n => n.notificationId !== id)
          );
        }, 5000)
      );
      this.notificationsForCenterSubj$.next(
        this.notificationsForCenterSubj$.getValue().map(n => ({ ...n, deleted: n.deleted || n.notificationId === id }))
      );
    });
  }

  public undoDismissFromCenter(id: string): void {
    clearTimeout(this.recentlyDeleted.get(id));
    this.recentlyDeleted.delete(id);
    this.notificationsApi
      .hideBulk([id], true)
      .subscribe(() =>
        this.notificationsForCenterSubj$.next(
          this.notificationsForCenterSubj$
            .getValue()
            .map(n => ({ ...n, deleted: n.notificationId === id ? false : n.deleted }))
        )
      );
  }

  public clearRecentlyDeleted() {
    const ids = [];
    this.recentlyDeleted.forEach((timerId, notificationId) => {
      clearTimeout(this.recentlyDeleted.get(timerId));
      ids.push(notificationId);
    });
    this.notificationsForCenterSubj$.next(
      this.notificationsForCenterSubj$.getValue().filter(n => !ids.includes(n.notificationId))
    );
    this.recentlyDeleted.clear();
  }

  private reloadNotificationsForCenter() {
    this.notificationsForCenterTrigger$.next(1);
  }

  private pushPolling(countryCode) {
    return timer(0, 5000).pipe(
      switchMap(() => this.hideNewPushNotifications()),
      switchMap(() =>
        this.notificationsApi.poll(countryCode, [
          NotificationType.Alert,
          NotificationType.Notification,
          NotificationType['Urgent alert']
        ])
      )
    );
  }

  private centerPolling(countryCode) {
    return merge(timer(0, 60000), this.notificationsForCenterTrigger$.asObservable()).pipe(
      filter(() => !this.recentlyDeleted.size),
      switchMap(() =>
        this.notificationsApi.getList(
          countryCode,
          [NotificationType.Alert, NotificationType.Notification],
          [UserNotificationStatus.New, UserNotificationStatus.Hidden],
          true
        )
      )
    );
  }

  private hideNewPushNotifications() {
    const newPushesIds = this.pushNotificationsSubj$
      .getValue()
      .filter(
        i =>
          i.status === UserNotificationStatus.New &&
          (i.type === NotificationType.Alert || i.type === NotificationType.Notification)
      )
      .map(i => i.notificationId);
    return newPushesIds.length
      ? this.notificationsApi.hideBulk(newPushesIds, true).pipe(
          tap(() => {
            this.reloadNotificationsForCenter();
            this.pushNotificationsSubj$.next(
              this.pushNotificationsSubj$.getValue().map(i => ({
                ...i,
                status: newPushesIds.includes(i.notificationId) ? UserNotificationStatus.Hidden : i.status
              }))
            );
          }),
          catchError(() => of({})) // need catch to not fail timer
        )
      : of({});
  }
}
